import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, History, Power, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Card, CardContent } from '../ui/card';
import RuleFormDialog from './RuleFormDialog';
import { ACTION_META, API, OPERATOR_LABELS, FIELD_LABELS, errorText, planSummary } from './constants';

const RuleVersions = ({ companyId, rule }) => {
  const { data } = useQuery({
    queryKey: ['v2-rule-versions', rule.id, rule.version],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/institutional-rules/${rule.id}/versions`)).data,
  });
  return (
    <div className="mt-3 space-y-1 border-t border-slate-200 pt-3 dark:border-slate-700" data-testid={`rule-versions-${rule.id}`}>
      {(data?.versions || []).map((v) => (
        <div key={v.id} className="text-xs text-slate-500">v{v.version} · {v.name} · {new Date(v.updated_at).toLocaleString()} · {v.updated_by || v.created_by}</div>
      ))}
    </div>
  );
};

const RuleCard = ({ companyId, rule, onEdit }) => {
  const qc = useQueryClient();
  const [showVersions, setShowVersions] = useState(false);
  const toggle = useMutation({
    mutationFn: (active) => axios.patch(`${API}/companies/${companyId}/institutional-rules/${rule.id}/toggle?is_active=${active}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['v2-rules', companyId] }); toast.success('Regla actualizada'); },
    onError: (e) => toast.error(errorText(e)),
  });
  const del = useMutation({
    mutationFn: () => axios.delete(`${API}/companies/${companyId}/institutional-rules/${rule.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['v2-rules', companyId] }); toast.success('Regla eliminada (su historial de versiones se conserva)'); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Card data-testid={`rule-card-${rule.id}`} className="transition-shadow duration-200 hover:shadow-md">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="font-semibold text-slate-900 dark:text-white">{rule.name}</h4>
              <Badge variant="outline">v{rule.version}</Badge>
              <Badge className={ACTION_META[rule.action || 'require_approval'].cls} data-testid={`rule-action-${rule.id}`}>{ACTION_META[rule.action || 'require_approval'].label}</Badge>
              <Badge className="bg-amber-100 text-amber-800">{rule.severity}</Badge>
              {(rule.action || 'require_approval') === 'require_approval' && <Badge className="bg-slate-100 text-slate-700" data-testid={`rule-plan-${rule.id}`}>{planSummary(rule)}</Badge>}
              {rule.action === 'block' && <Badge variant="outline">{rule.allow_exception ? `Excepción: ${rule.exception_role === 'admin' ? 'Admin' : 'Super Admin'}` : 'Sin excepciones'}</Badge>}
            </div>
            {rule.description && <p className="mt-1 text-sm text-slate-500">{rule.description}</p>}
            <div className="mt-2 flex flex-wrap gap-2">
              {rule.conditions.map((c, i) => (
                <code key={i} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                  {FIELD_LABELS[c.field]} {OPERATOR_LABELS[c.operator]} {Array.isArray(c.value) ? c.value.join(', ') : String(c.value)}
                </code>
              ))}
              {(rule.action || 'require_approval') === 'require_approval' && <span className="text-xs text-slate-400">· expira en {rule.expires_in_hours}h</span>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Power className="h-4 w-4 text-slate-400" />
            <Switch checked={rule.is_active} onCheckedChange={(v) => toggle.mutate(v)} data-testid={`rule-toggle-${rule.id}`} />
            <Button size="sm" variant="ghost" onClick={() => setShowVersions((s) => !s)} data-testid={`rule-versions-btn-${rule.id}`}><History className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => onEdit(rule)} data-testid={`rule-edit-btn-${rule.id}`}>Editar</Button>
            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600" onClick={() => window.confirm('¿Eliminar esta regla?') && del.mutate()} data-testid={`rule-delete-btn-${rule.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </div>
        {showVersions && <RuleVersions companyId={companyId} rule={rule} />}
      </CardContent>
    </Card>
  );
};

export const RulesManager = ({ companyId }) => {
  const [editing, setEditing] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ['v2-rules', companyId],
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/institutional-rules`)).data,
    enabled: !!companyId,
  });
  const rules = data?.rules || [];
  return (
    <div className="space-y-4" data-testid="rules-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Las reglas se evalúan de forma determinística sobre cada decisión propuesta. Si coinciden varias: Bloquear prevalece sobre Requiere aprobación, luego Advertir y Registrar. Cada edición crea una nueva versión.</p>
        <Button onClick={() => setEditing({})} data-testid="rule-create-btn"><Plus className="mr-2 h-4 w-4" />Nueva regla</Button>
      </div>
      {isLoading && <p className="text-sm text-slate-400">Cargando reglas…</p>}
      {!isLoading && rules.length === 0 && <p className="text-sm text-slate-400" data-testid="rules-empty">Aún no hay reglas institucionales.</p>}
      {rules.map((r) => <RuleCard key={r.id} companyId={companyId} rule={r} onEdit={setEditing} />)}
      {editing && <RuleFormDialog companyId={companyId} rule={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
};
